/**
 * ResendStatusBadge · Architect-only Resend domain verification indicator.
 *
 * Polls /api/domains/resend every 60s. Renders a small pill with the
 * verification state (VERIFIED / PROPAGATING / DNS NEEDED / OFFLINE) and the
 * canonical next-action sentence so the Architect knows exactly what to do
 * without leaving the Console.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

const TONE = {
  verified:       { color: "green",   label: "RESEND · VERIFIED" },
  pending:        { color: "gold",    label: "RESEND · PROPAGATING" },
  not_started:    { color: "magenta", label: "RESEND · DNS NEEDED" },
  failed:         { color: "red",     label: "RESEND · DNS FAILED" },
  no_domain:      { color: "magenta", label: "RESEND · NO DOMAIN" },
  not_configured: { color: "red",     label: "RESEND · NO API KEY" },
  unknown:        { color: "cyan",    label: "RESEND · CHECKING…" },
};

export default function ResendStatusBadge() {
  const [state, setState] = useState({ status: "unknown" });

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const d = await api.resendStatus();
        if (alive) setState(d);
      } catch {
        if (alive) setState({ status: "unknown" });
      }
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const tone = TONE[state.status] || TONE.unknown;
  return (
    <div
      data-testid="resend-status-badge"
      className="inline-flex items-center gap-2 px-2.5 py-1 border font-mono text-[10px] uppercase tracking-[0.24em]"
      style={{
        borderColor: `var(--${tone.color})`,
        color: `var(--${tone.color})`,
        textShadow: `0 0 6px var(--${tone.color})`,
      }}
      title={`Sender: ${state.sender_email || "—"} · ${state.action || ""}`}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ background: `var(--${tone.color})`, boxShadow: `0 0 6px var(--${tone.color})` }}
      />
      <span>{tone.label}</span>
      {state.name && state.status !== "no_domain" && state.status !== "not_configured" && (
        <span className="opacity-70">· {state.name}</span>
      )}
    </div>
  );
}
